import React, { useEffect, useState } from 'react';
import ReactQuill from 'react-quill-new';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@mui/material';

import { selectSortedCommunicationTemplates } from '../../../../store/communicationSelectors';
import {
    clearCommunicationTemplates,
    clearCurrentTemplate,
    loadCommunicationTemplate,
    previewGroupEmailCommunication,
    searchCommunicationTemplates,
    sendGroupEmailCommunication
} from '../../../../store/communicationService';
import { searchParticipants } from '../../../../store/participantService';
import {
    ADVISER,
    hasRole,
    MANAGER,
    QUALITY,
    RECRUITMENT_MANAGER,
    SUPERUSER
} from '../../../../utils/userRoles';
import CommunicationsErrorModal from '../../../communication/CommunicationsErrorModal';
import IconError from '../../../IconError';
import LabelledTextField from '../../../ui/editors/LabelledTextField';
import SensitivityNotice from '../../../ui/notices/sensitivity/SensitivityNotice';
import DDLOptionPicker from '../../../ui/pickers/DDLOptionPicker';

import { initialErrorState, validateGroupEmail } from './validateGroupCommunications';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';
import local from '../communicationStyles.module.css';

const GroupEmail = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { state } = useLocation();

    // LOCAL STATE
    const TIME = 30;
    const acceptedRoles = [ADVISER, MANAGER, QUALITY, RECRUITMENT_MANAGER, SUPERUSER];
    const initialState = {
        templateId: '',
        templateName: '',
        emailAddresses: '',
        subject: '',
        details: '',
        signature: '',
        sigLen: 0
    };

    const [newEntry, setNewEntry] = useState(initialState);
    const [errors, setErrors] = useState(initialErrorState);
    const [showData, setShowData] = useState(false);
    const [disableSendNowButton, setDisableSendNowButton] = useState(false);
    const [placeholderErrorOpen, setPlaceholderErrorOpen] = useState(false);
    const msg = `Email Communication has been sent to ${state.group.name}`;

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const { roles } = loggedInUser;
    const participants = useSelector(
        (state) => state.entities.participantService.participantsSearch
    );
    const templates = useSelector(selectSortedCommunicationTemplates);
    const currentTemplate = useSelector(
        (state) => state.entities.communicationService.communicationTemplate
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USE EFFECTS
    useEffect(
        () => () => {
            cleanUp();
        },
        []
    );

    useEffect(() => {
        if (state?.group?.participants?.length < 1) return;
        const ids = state.group.participants.map((el) => el.participantId);
        ids.length && dispatch(searchParticipants({ ids }, 0));
    }, [state]);

    useEffect(() => {
        if (!loggedInUser?.id) return;
        const name = `<p><br></p><p>${loggedInUser?.firstName} ${loggedInUser?.lastName}</p>`;
        const job = `<p>${loggedInUser?.jobTitle}</p>`;
        const phone = `<p>${loggedInUser?.phoneNumber}</p>`;
        const signature = `${name}${loggedInUser?.jobTitle ? job : ''}${
            loggedInUser?.phoneNumber ? phone : ''
        }`;
        const sigLen = signature.length;
        setNewEntry((prev) => ({
            ...prev,
            signature,
            sigLen,
            details: newEntry.details.length ? newEntry.details : newEntry.details.concat(signature)
        }));
    }, [loggedInUser]);

    useEffect(() => {
        if (!participants || participants.length < 1) return;
        const emailAddresses = participants
            .map((el) =>
                state.group.participants.find((entry) => entry.participantId === el.id)
                    ?.contactMode === 'EMAIL'
                    ? el.emailAddress
                    : ''
            )
            .join('; ');

        setNewEntry((prev) => ({ ...prev, emailAddresses }));
        const serviceIds = participants.map((el) => el.serviceId);
        dispatch(
            searchCommunicationTemplates({
                type: 'EMAIL',
                serviceIds: [...new Set(serviceIds)],
                inactive: false
            })
        );
    }, [participants]);

    useEffect(() => {
        if (!newEntry.templateId || Object.keys(currentTemplate).length < 1) return;
        setNewEntry((prev) => ({
            ...prev,
            subject: currentTemplate.subject,
            details: currentTemplate.details.concat(newEntry.signature)
        }));
    }, [newEntry.templateId, currentTemplate]);

    useEffect(() => {
        if (successMessage === msg) {
            setDisableSendNowButton(false);
            clearForm();
            const timeoutId = setTimeout(() => navigate('/manage_groups'), 2000);
            return () => clearTimeout(timeoutId);
        }
    }, [successMessage]);

    // HELPER FNS
    const clearForm = () => {
        setNewEntry(initialState);
        setErrors(initialErrorState);
        dispatch(clearCurrentTemplate());
    };

    const clearError = (key) => {
        setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const cleanUp = () => {
        clearForm();
        dispatch(clearCommunicationTemplates());
    };

    // EVENT HANDLERS
    const onRevealData = () => {
        setShowData(!showData);
        setTimeout(() => setShowData((showData) => !showData), TIME * 1000);
    };

    const onDetailsChange = (details) => {
        clearError('details');
        setNewEntry((prev) => ({ ...prev, details }));
    };

    const onPreviewClick = () => {
        const { isValid, newErrors } = validateGroupEmail(newEntry, errors);
        setErrors(newErrors);
        if (!isValid) return;
        setPlaceholderErrorOpen(true);
    };

    const onSubmit = () => {
        setDisableSendNowButton(true);
        const { subject, details } = newEntry;
        const payload = { subject, body: details };
        dispatch(sendGroupEmailCommunication(payload, state.group.id, msg));
    };

    return (
        <div className={app.pageContainer} id="emailScreen">
            <CommunicationsErrorModal
                isOpen={placeholderErrorOpen}
                groupId={state.group.id}
                participants={participants}
                payload={{ subject: newEntry.subject, body: newEntry.details }}
                previewCommunication={previewGroupEmailCommunication}
                onAccept={onSubmit}
                onClose={() => setPlaceholderErrorOpen(false)}
            />
            <h1 className={app.mainHeading}>Communications</h1>
            <form className={`${form.formWrapper} ${local.formContainer}`}>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <div className={form.staticText}>
                            <div className={form.formLabel}>To Email Addresses</div>
                            <div className={`${form.textBox} ${form.alignLeft}`}>
                                {newEntry.emailAddresses}
                            </div>
                        </div>

                        {!showData && (
                            <div className={`${form.inputCover} ${form.coverHeightSm}`}>
                                <SensitivityNotice
                                    onClick={onRevealData}
                                    numRows={1}
                                    customClass="inputNotice"
                                />
                            </div>
                        )}
                    </div>
                </div>
                <div className={`${form.formSection} ${form.alignDownAlot}`}>
                    <div className={form.formColumn}>
                        <DDLOptionPicker
                            label={'Template'}
                            id={'templates'}
                            placeholder="Template"
                            disabled={!hasRole(acceptedRoles, roles)}
                            menuItems={templates || []}
                            chosenName={newEntry.templateName}
                            chosenId={newEntry.templateId}
                            onChange={(chosenId) => {
                                chosenId && dispatch(loadCommunicationTemplate(chosenId));
                                const chosenTemplate = templates?.find((el) => el.id === chosenId);
                                setNewEntry((prev) => ({
                                    ...prev,
                                    templateId: chosenId || '',
                                    templateName: chosenTemplate?.name || ''
                                }));
                            }}></DDLOptionPicker>
                    </div>
                    <div className={form.formColumn}>
                        <LabelledTextField
                            label={'Email Subject'}
                            id={'subject'}
                            mandatory={true}
                            disabled={!hasRole(acceptedRoles, roles)}
                            value={newEntry.subject}
                            placeholder={'Enter email subject'}
                            onChange={(e) => {
                                clearError('subject');
                                setNewEntry((prev) => ({
                                    ...prev,
                                    subject: e.target.value
                                }));
                            }}
                        />
                        {errors.subject.error && (
                            <div className={form.textInputError}>
                                <IconError text={errors.subject} />
                            </div>
                        )}
                    </div>
                </div>

                <div className={`${form.formSection} ${form.alignDown}`}>
                    <div className={form.formColumn}>
                        <div className={form.textAreaSet}>
                            <ReactQuill
                                value={newEntry.details}
                                modules={GroupEmail.modules}
                                placeholder="Enter details"
                                onChange={onDetailsChange}
                                readOnly={!hasRole(acceptedRoles, roles)}
                            />
                            {errors.details.error && (
                                <div className={`${form.textInputError} ${form.resetMargin}`}>
                                    <IconError text={errors.details} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className={form.buttonRight}>
                    <Button
                        type="submit"
                        color="primary"
                        variant="contained"
                        onClick={(e) => {
                            e.preventDefault();
                            onPreviewClick();
                        }}
                        disabled={
                            !hasRole(acceptedRoles, roles) ||
                            newEntry.details.length < 1 ||
                            disableSendNowButton ||
                            Object.values(errors).some((el) => el.error)
                        }>
                        Send Now
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default GroupEmail;

GroupEmail.modules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ align: '' }, { align: 'center' }, { align: 'right' }],
        ['clean']
    ]
};
